import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import LandingPage from './pages/LandingPage';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import Dashboard from './pages/Dashboard';
import Trading from './pages/Trading';
import TradingNew from './pages/TradingNew';
import Wallet from './pages/Wallet';
import WalletNew from './pages/WalletNew';
import Portfolio from './pages/Portfolio';
import Swap from './pages/Swap';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminLayout from './pages/admin/AdminLayout';
import AdminAnalyticsDashboard from './pages/admin/AdminAnalyticsDashboard';
import UsersManagement from './pages/admin/UsersManagement';
import KYCManagement from './pages/admin/KYCManagement';
import KYCApplication from './pages/KYCApplication';
import DepositsManagement from './pages/admin/DepositsManagement';
import WithdrawalsManagement from './pages/admin/WithdrawalsManagement';
import TradingControl from './pages/admin/TradingControl';
import SettingsManagement from './pages/admin/SettingsManagement';
import AdminUsersManagement from './pages/admin/AdminUsersManagement';
import AuditLogs from './pages/admin/AuditLogs';
import PlatformDepositAddresses from './pages/admin/PlatformDepositAddresses';
import SystemAudit from './pages/admin/SystemAudit';
import LiveChatManagement from './pages/admin/LiveChatManagement';
import LegacyAuthAudit from './pages/admin/LegacyAuthAudit';
import BalanceSyncHealth from './pages/admin/BalanceSyncHealth';
import MobileNav from './components/MobileNav';
import LiveChatWidget from './components/LiveChatWidget';
import TradeWatcher from './components/trade-watcher/TradeWatcher';
import AwaySummary from './components/trade-watcher/AwaySummary';
import AdminRoute from './auth/AdminRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { RealtimeProvider } from './context/RealtimeContext';
import { AdminRealtimeProvider } from './context/AdminRealtimeContext';
import ConnectionMonitor from './components/ConnectionMonitor';
import { initializeCacheManager } from './utils/cacheManager';
import './App.css';

// Initialize cache manager on app load
initializeCacheManager();

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/signin" />;
};

// User routes component - wrapped with AuthContext and RealtimeProvider
const UserRoutes = () => {
  const location = useLocation();
  const { user } = useAuth();
  
  // Show mobile nav only on authenticated user pages
  const showMobileNav = user && ['/dashboard', '/trading', '/portfolio', '/wallet', '/swap', '/settings'].includes(location.pathname);

  // Wrap authenticated routes with RealtimeProvider for live data sync
  const userId = user?.id || user?.user_id;

  return (
    <>
      {userId && <ConnectionMonitor />}
      {userId && <TradeWatcher />}
      {userId && <AwaySummary key={userId} />}
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <RealtimeProvider userId={userId}>
                <Dashboard />
              </RealtimeProvider>
            </PrivateRoute>
          }
        />
        <Route
          path="/trading"
          element={
            <PrivateRoute>
              <RealtimeProvider userId={userId}>
                <TradingNew />
              </RealtimeProvider>
            </PrivateRoute>
          }
        />
        <Route
          path="/trading-old"
          element={
            <PrivateRoute>
              <Trading />
            </PrivateRoute>
          }
        />
        <Route
          path="/swap"
          element={
            <PrivateRoute>
              <RealtimeProvider userId={userId}>
                <Swap />
              </RealtimeProvider>
            </PrivateRoute>
          }
        />
        <Route
          path="/wallet"
          element={
            <PrivateRoute>
              <WalletNew />
            </PrivateRoute>
          }
        />
        <Route
          path="/wallet-old"
          element={
            <PrivateRoute>
              <Wallet />
            </PrivateRoute>
          }
        />
        <Route
          path="/portfolio"
          element={<Navigate to="/wallet" replace />}
        />
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <RealtimeProvider userId={userId}>
                <Profile />
              </RealtimeProvider>
            </PrivateRoute>
          }
        />
        <Route
          path="/kyc"
          element={
            <PrivateRoute>
              <KYCApplication />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <PrivateRoute>
              <Settings />
            </PrivateRoute>
          }
        />
      </Routes>
      {showMobileNav && <MobileNav />}
      <LiveChatWidget />
      <Toaster />
    </>
  );
};

// Admin routes component - NO AuthContext dependency, wrapped with AdminRealtimeProvider
const AdminRoutes = () => {
  // Check if admin is logged in for realtime provider
  const adminToken = localStorage.getItem('adminToken');
  
  return (
    <>
      {adminToken && <ConnectionMonitor />}
      <Routes>
        {/* Admin routes - Completely isolated from user authentication */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route 
          path="/admin/old" 
          element={
            <AdminRoute>
              <AdminRealtimeProvider>
                <AdminDashboard />
              </AdminRealtimeProvider>
            </AdminRoute>
          } 
        />
        
        {/* New Admin Panel with Layout - Wrapped with real-time provider */}
        <Route 
          path="/admin" 
          element={
            <AdminRoute>
              <AdminRealtimeProvider>
                <AdminLayout />
              </AdminRealtimeProvider>
            </AdminRoute>
          }
        >
          <Route path="dashboard" element={<AdminAnalyticsDashboard />} />
          <Route path="users" element={<UsersManagement />} />
          <Route path="kyc" element={<KYCManagement />} />
          <Route path="deposits" element={<DepositsManagement />} />
          <Route path="withdrawals" element={<WithdrawalsManagement />} />
          <Route path="trading" element={<TradingControl />} />
          <Route path="settings" element={<SettingsManagement />} />
          <Route path="admins" element={<AdminUsersManagement />} />
          <Route path="audit" element={<AuditLogs />} />
          <Route path="deposit-addresses" element={<PlatformDepositAddresses />} />
          <Route path="system-audit" element={<SystemAudit />} />
          <Route path="live-chat" element={<LiveChatManagement />} />
          <Route path="legacy-audit" element={<LegacyAuthAudit />} />
          <Route path="balance-sync" element={<BalanceSyncHealth />} />
        </Route>
      </Routes>
      <Toaster />
    </>
  );
};

// Main layout router
const AppLayout = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Completely separate routing for admin vs user
  return isAdminRoute ? <AdminRoutes /> : <UserRoutes />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App">
            <AppLayout />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
